import React from 'react';
import { FileText, ShoppingBag, CreditCard, Download, Clock } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ZodiacTheme } from './zodiacThemes';
import { User } from '../../types';
interface OverviewTabProps {
  theme: ZodiacTheme;
  user: User | null;
  setActiveTab: (tab: string) => void;
}
export function OverviewTab({
  theme,
  user,
  setActiveTab
}: OverviewTabProps) {
  return <div className="space-y-6" data-id="element-17776">
      {/* Themed Welcome Banner */}
      <div className={`rounded-3xl bg-gradient-to-r ${theme.gradient} p-8 text-white shadow-lg relative overflow-hidden`} data-id="element-17777">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 text-[180px] opacity-10 pointer-events-none leading-none" data-id="element-17778">
          {theme.icon}
        </div>
        <div className="relative z-10" data-id="element-17779">
          <div className="flex items-center gap-2 mb-2" data-id="element-17780">
            <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider backdrop-blur-sm" data-id="element-17781">
              {theme.name} • {theme.element}
            </span>
          </div>
          <h2 className="text-3xl font-bold mb-2" data-id="element-17782">
            Добро пожаловать в вашу вселенную, {user?.name || 'Гость'}!
          </h2>
          <p className="text-white/80 max-w-xl" data-id="element-17783">
            Ваша натальная карта активна. Сегодня Луна в гармоничном аспекте с
            вашим Солнцем — отличный день для новых начинаний.
          </p>
          <div className="mt-6 flex gap-3" data-id="element-17784">
            <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" onClick={() => setActiveTab('calendar')} data-id="element-17785">
              Открыть календарь
            </Button>
            <Button variant="outline" className="bg-white text-slate-900 border-none hover:bg-slate-100" onClick={() => setActiveTab('profile')} data-id="element-17786">
              Мой профиль
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-17787">
        <Card padding="md" className="flex items-center hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab('reports')} data-id="element-17788">
          <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center mr-4`} data-id="element-17789">
            <FileText className="w-6 h-6" data-id="element-17790" />
          </div>
          <div data-id="element-17791">
            <div className="text-sm text-slate-500 font-medium" data-id="element-17792">
              Готовых отчётов
            </div>
            <div className="text-2xl font-bold text-slate-900" data-id="element-17793">3</div>
          </div>
        </Card>
        <Card padding="md" className="flex items-center hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab('credits')} data-id="element-17794">
          <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center mr-4`} data-id="element-17795">
            <CreditCard className="w-6 h-6" data-id="element-17796" />
          </div>
          <div data-id="element-17797">
            <div className="text-sm text-slate-500 font-medium" data-id="element-17798">
              Доступно кредитов
            </div>
            <div className="text-2xl font-bold text-slate-900" data-id="element-17799">2</div>
          </div>
        </Card>
        <Card padding="md" className="flex items-center hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab('orders')} data-id="element-17800">
          <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center mr-4`} data-id="element-17801">
            <ShoppingBag className="w-6 h-6" data-id="element-17802" />
          </div>
          <div data-id="element-17803">
            <div className="text-sm text-slate-500 font-medium" data-id="element-17804">
              Активных заказов
            </div>
            <div className="text-2xl font-bold text-slate-900" data-id="element-17805">1</div>
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8" data-id="element-17806">
        <Card padding="lg" className={`border-t-4 ${theme.border.replace('border-', 'border-t-')}`} data-id="element-17807">
          <div className="flex justify-between items-center mb-6" data-id="element-17808">
            <h3 className="font-bold text-slate-900" data-id="element-17809">Последние отчёты</h3>
            <button onClick={() => setActiveTab('reports')} className={`text-sm ${theme.text} hover:underline`} data-id="element-17810">
              Все
            </button>
          </div>
          <div className="space-y-4" data-id="element-17811">
            {[1, 2].map(i => <div key={i} className="flex items-center justify-between p-4 border border-slate-100 rounded-xl hover:border-slate-200 transition-colors" data-id="element-17812">
                <div className="flex items-center" data-id="element-17813">
                  <div className={`w-10 h-10 rounded-lg ${theme.bg} flex items-center justify-center mr-4`} data-id="element-17814">
                    <FileText className={`w-5 h-5 ${theme.text}`} data-id="element-17815" />
                  </div>
                  <div data-id="element-17816">
                    <div className="font-medium text-slate-900" data-id="element-17817">
                      {i === 1 ? 'Натальная карта' : 'Соляр 2024'}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-17818">
                      12 Октября 2024
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" icon={<Download className="w-4 h-4" data-id="element-17820" />} data-id="element-17819">
                  PDF
                </Button>
              </div>)}
          </div>
        </Card>

        <Card padding="lg" data-id="element-17821">
          <div className="flex justify-between items-center mb-6" data-id="element-17822">
            <h3 className="font-bold text-slate-900" data-id="element-17823">Текущий заказ</h3>
            <button onClick={() => setActiveTab('orders')} className={`text-sm ${theme.text} hover:underline`} data-id="element-17824">
              Все
            </button>
          </div>
          <div className="border border-slate-100 rounded-xl p-5" data-id="element-17825">
            <div className="flex justify-between items-start mb-4" data-id="element-17826">
              <div data-id="element-17827">
                <Badge variant="warning" className="mb-2" data-id="element-17828">
                  В работе
                </Badge>
                <div className="font-bold text-slate-900" data-id="element-17829">
                  Консультация: Синастрия
                </div>
              </div>
              <div className="text-right" data-id="element-17830">
                <div className="text-sm font-medium text-slate-900" data-id="element-17831">
                  Елена Смирнова
                </div>
                <div className="text-xs text-slate-500" data-id="element-17832">Видеозвонок</div>
              </div>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg flex items-center text-sm text-slate-600 mb-4" data-id="element-17833">
              <Clock className="w-4 h-4 mr-2 text-amber-500" data-id="element-17834" /> Ожидайте ссылку
              на звонок 15 Октября в 14:00
            </div>
            <Button variant="outline" fullWidth onClick={() => setActiveTab('chat')} data-id="element-17835">
              Открыть чат
            </Button>
          </div>
        </Card>
      </div>
    </div>;
}